import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import LoginModal from '@/components/LoginModal'
import { 
  ArrowRight,
  Copy,
  CheckCircle,
  ExternalLink,
  FileText,
  MessageSquare,
  Users,
  Headphones
} from 'lucide-react'

// 快速开始步骤
const quickStartSteps = [
  {
    step: 1,
    title: '注册账号',
    description: '创建您的SMS云平台账号',
    icon: Users,
    details: [
      '访问注册页面',
      '填写基本信息',
      '验证邮箱地址',
      '完成账号激活'
    ]
  },
  {
    step: 2,
    title: '获取API密钥',
    description: '在控制台生成您的API密钥',
    icon: Users,
    details: [
      '登录管理控制台',
      '进入API管理页面',
      '创建新的API密钥',
      '保存密钥信息'
    ]
  },
  {
    step: 3,
    title: '创建短信模板',
    description: '设置您的短信发送模板',
    icon: FileText,
    details: [
      '选择模板类型',
      '编写模板内容',
      '提交审核申请',
      '等待审核通过'
    ]
  },
  {
    step: 4,
    title: '发送第一条短信',
    description: '调用API发送您的第一条短信',
    icon: MessageSquare,
    details: [
      '选择编程语言',
      '安装SDK或直接调用API',
      '编写发送代码',
      '测试发送功能'
    ]
  }
]

// API文档分类
const apiCategories = {
  authentication: {
    title: '身份认证',
    description: 'API访问认证方式',
    endpoints: [
      {
        method: 'POST',
        path: '/api/v1/auth/login',
        title: '用户登录',
        description: '获取访问令牌'
      },
      {
        method: 'POST',
        path: '/api/v1/auth/refresh',
        title: '刷新令牌',
        description: '刷新访问令牌'
      }
    ]
  },
  sms: {
    title: '短信发送',
    description: '短信发送相关API',
    endpoints: [
      {
        method: 'POST',
        path: '/api/v1/sms/send',
        title: '发送短信',
        description: '发送单条或批量短信'
      },
      {
        method: 'GET',
        path: '/api/v1/sms/status/{id}',
        title: '查询状态',
        description: '查询短信发送状态'
      },
      {
        method: 'GET',
        path: '/api/v1/sms/reports',
        title: '发送报告',
        description: '获取短信发送报告'
      }
    ]
  },
  templates: {
    title: '模板管理',
    description: '短信模板管理API',
    endpoints: [
      {
        method: 'POST',
        path: '/api/v1/templates',
        title: '创建模板',
        description: '创建新的短信模板'
      },
      {
        method: 'GET',
        path: '/api/v1/templates',
        title: '获取模板',
        description: '获取模板列表'
      },
      {
        method: 'PUT',
        path: '/api/v1/templates/{id}',
        title: '更新模板',
        description: '更新模板内容'
      }
    ]
  }
}

// SDK支持
const sdkSupport = [
  {
    language: 'Java',
    icon: '☕',
    version: '1.2.0',
    features: ['Maven依赖', 'Spring Boot集成', '异步发送', '批量处理'],
    install: 'mvn install sms-platform-java-sdk',
    color: 'bg-orange-100 text-orange-800'
  },
  {
    language: 'Python',
    icon: '🐍',
    version: '2.1.0',
    features: ['pip安装', 'Django集成', '异步支持', '类型提示'],
    install: 'pip install sms-platform-python',
    color: 'bg-blue-100 text-blue-800'
  },
  {
    language: 'PHP',
    icon: '🐘',
    version: '1.5.0',
    features: ['Composer支持', 'Laravel集成', '队列处理', '缓存优化'],
    install: 'composer require sms-platform/php-sdk',
    color: 'bg-purple-100 text-purple-800'
  },
  {
    language: 'Node.js',
    icon: '🟢',
    version: '3.0.0',
    features: ['npm安装', 'Express集成', 'Promise支持', 'TypeScript'],
    install: 'npm install sms-platform-node',
    color: 'bg-green-100 text-green-800'
  },
  {
    language: 'Go',
    icon: '🐹',
    version: '1.0.0',
    features: ['go mod支持', 'Gin集成', '并发处理', '性能优化'],
    install: 'go get github.com/sms-platform/go-sdk',
    color: 'bg-cyan-100 text-cyan-800'
  },
  {
    language: 'C#',
    icon: '🔷',
    version: '1.3.0',
    features: ['NuGet包', 'ASP.NET集成', '异步支持', '依赖注入'],
    install: 'Install-Package SmsPlatform.CSharp',
    color: 'bg-indigo-100 text-indigo-800'
  }
]

// 代码示例
const codeExamples = {
  java: {
    language: 'Java',
    code: `import com.smsplatform.SmsClient;
import com.smsplatform.model.SendSmsRequest;
import com.smsplatform.model.SendSmsResponse;

public class SmsExample {
    public static void main(String[] args) {
        // 初始化客户端
        SmsClient client = new SmsClient("your-api-key");
        
        // 构建发送请求
        SendSmsRequest request = SendSmsRequest.builder()
            .phoneNumber("13800138000")
            .templateId("template_123")
            .params(Map.of("code", "123456"))
            .build();
        
        // 发送短信
        SendSmsResponse response = client.sendSms(request);
        
        System.out.println("发送结果: " + response.getMessage());
    }
}`
  },
  python: {
    language: 'Python',
    code: `from sms_platform import SmsClient

# 初始化客户端
client = SmsClient(api_key="your-api-key")

# 发送短信
response = client.send_sms(
    phone_number="13800138000",
    template_id="template_123",
    params={"code": "123456"}
)

print(f"发送结果: {response.message}")`
  },
  php: {
    language: 'PHP',
    code: `<?php
use SmsPlatform\\SmsClient;

// 初始化客户端
$client = new SmsClient('your-api-key');

// 发送短信
$response = $client->sendSms([
    'phone_number' => '13800138000',
    'template_id' => 'template_123',
    'params' => ['code' => '123456']
]);

echo "发送结果: " . $response->getMessage();
?>`
  },
  nodejs: {
    language: 'Node.js',
    code: `const { SmsClient } = require('sms-platform-node');

// 初始化客户端
const client = new SmsClient('your-api-key');

// 发送短信
async function sendSms() {
    try {
        const response = await client.sendSms({
            phoneNumber: '13800138000',
            templateId: 'template_123',
            params: { code: '123456' }
        });
        
        console.log('发送结果:', response.message);
    } catch (error) {
        console.error('发送失败:', error);
    }
}

sendSms();`
  }
}

// 常见问题
const faqs = [
  {
    question: '如何获取API密钥？',
    answer: '登录管理控制台，进入"API管理"页面，点击"创建API密钥"即可生成新的密钥。请妥善保管您的API密钥，不要泄露给他人。'
  },
  {
    question: '短信模板审核需要多长时间？',
    answer: '一般情况下，短信模板审核时间为1-2个工作日。我们会尽快处理您的审核申请，请耐心等待。'
  },
  {
    question: '支持哪些编程语言？',
    answer: '我们提供Java、Python、PHP、Node.js、Go、C#等多种编程语言的SDK，您可以根据项目需求选择合适的语言。'
  },
  {
    question: '如何查看发送报告？',
    answer: '您可以通过API接口或管理控制台查看详细的发送报告，包括发送状态、失败原因等信息。'
  },
  {
    question: '是否支持国际短信？',
    answer: '是的，我们支持200+个国家和地区的国际短信发送，包括美国、欧洲、东南亚等主要地区。'
  },
  {
    question: '如何联系技术支持？',
    answer: '我们提供7x24小时技术支持服务，您可以通过在线客服、邮件或电话联系我们。'
  }
]

export default function DocsPage() {
  const [showLoginModal, setShowLoginModal] = useState(false)
  const [activeTab, setActiveTab] = useState('quickstart')
  const [activeCodeExample, setActiveCodeExample] = useState('java')
  const [copiedCode, setCopiedCode] = useState('')

  const copyToClipboard = (code: string, language: string) => {
    navigator.clipboard.writeText(code)
    setCopiedCode(language)
    setTimeout(() => setCopiedCode(''), 2000)
  }

  return (
    <div className="space-y-20">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-blue-50 via-white to-purple-50">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center space-y-8">
            <div className="space-y-4">
              <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100">
                📚 开发者文档
              </Badge>
              <h1 className="text-4xl lg:text-6xl font-bold text-gray-900 leading-tight">
                开发者
                <span className="text-blue-600">文档中心</span>
              </h1>
              <p className="text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto">
                完整的API文档、SDK示例和集成指南，帮助您快速接入我们的短信服务
              </p>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                className="text-lg px-8 py-3"
                onClick={() => setShowLoginModal(true)}
              >
                开始集成
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button variant="outline" size="lg" className="text-lg px-8 py-3" asChild>
                <a href="#api-reference" target="_blank" rel="noopener noreferrer">
                  查看API文档
                  <ExternalLink className="ml-2 h-5 w-5" />
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* 文档导航 */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-4 mb-12">
            <TabsTrigger value="quickstart">快速开始</TabsTrigger>
            <TabsTrigger value="api">API文档</TabsTrigger>
            <TabsTrigger value="sdk">SDK集成</TabsTrigger>
            <TabsTrigger value="faq">常见问题</TabsTrigger>
          </TabsList>

          {/* 快速开始 */}
          <TabsContent value="quickstart" className="space-y-8">
            <div className="text-center space-y-4 mb-12">
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900">
                5分钟快速开始
              </h2>
              <p className="text-xl text-gray-600">
                按照以下步骤，快速集成我们的短信服务
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {quickStartSteps.map((step, index) => (
                <Card key={index} className="relative">
                  <div className="absolute -top-4 -left-4 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">
                    {step.step}
                  </div>
                  <CardHeader>
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                        <step.icon className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <CardTitle className="text-lg">{step.title}</CardTitle>
                        <CardDescription>{step.description}</CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {step.details.map((detail, detailIndex) => (
                        <li key={detailIndex} className="flex items-center space-x-2 text-sm text-gray-600">
                          <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                          <span>{detail}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* API文档 */}
          <TabsContent value="api" className="space-y-8">
            <div className="text-center space-y-4 mb-12">
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900">
                API 参考文档
              </h2>
              <p className="text-xl text-gray-600">
                完整的RESTful API接口文档
              </p>
            </div>
            
            <div className="space-y-8">
              {Object.entries(apiCategories).map(([key, category]) => (
                <Card key={key}>
                  <CardHeader>
                    <CardTitle className="text-2xl">{category.title}</CardTitle>
                    <CardDescription className="text-lg">{category.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {category.endpoints.map((endpoint, index) => (
                        <div key={index} className="border rounded-lg p-4">
                          <div className="flex items-center space-x-4 mb-2">
                            <Badge variant={endpoint.method === 'GET' ? 'default' : 'destructive'}>
                              {endpoint.method}
                            </Badge>
                            <code className="text-sm bg-gray-100 px-2 py-1 rounded">
                              {endpoint.path}
                            </code>
                          </div>
                          <h4 className="font-semibold text-lg">{endpoint.title}</h4>
                          <p className="text-gray-600">{endpoint.description}</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* SDK集成 */}
          <TabsContent value="sdk" className="space-y-8">
            <div className="text-center space-y-4 mb-12">
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900">
                SDK 支持
              </h2>
              <p className="text-xl text-gray-600">
                多语言SDK，简化集成过程
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
              {sdkSupport.map((sdk, index) => (
                <Card key={index} className="hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <div className="flex items-center space-x-3">
                      <div className="text-2xl">{sdk.icon}</div>
                      <div>
                        <CardTitle className="text-lg">{sdk.language}</CardTitle>
                        <Badge className={sdk.color}>v{sdk.version}</Badge>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div>
                        <h4 className="font-semibold mb-2">安装命令</h4>
                        <code className="text-sm bg-gray-100 p-2 rounded block">
                          {sdk.install}
                        </code>
                      </div>
                      <div>
                        <h4 className="font-semibold mb-2">主要特性</h4>
                        <ul className="space-y-1">
                          {sdk.features.map((feature, featureIndex) => (
                            <li key={featureIndex} className="flex items-center space-x-2 text-sm text-gray-600">
                              <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                              <span>{feature}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* 代码示例 */}
            <div className="space-y-8">
              <div className="text-center space-y-4">
                <h3 className="text-2xl font-bold text-gray-900">代码示例</h3>
                <p className="text-gray-600">选择您的编程语言，查看集成示例</p>
              </div>
              
              <div className="space-y-4">
                <div className="flex flex-wrap gap-2 justify-center">
                  {Object.keys(codeExamples).map((lang) => (
                    <Button
                      key={lang}
                      variant={activeCodeExample === lang ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setActiveCodeExample(lang)}
                    >
                      {codeExamples[lang as keyof typeof codeExamples].language}
                    </Button>
                  ))}
                </div>
                
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg">
                        {codeExamples[activeCodeExample as keyof typeof codeExamples].language} 示例
                      </CardTitle>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => copyToClipboard(
                          codeExamples[activeCodeExample as keyof typeof codeExamples].code,
                          activeCodeExample
                        )}
                      >
                        {copiedCode === activeCodeExample ? (
                          <>
                            <CheckCircle className="h-4 w-4 mr-2" />
                            已复制
                          </>
                        ) : (
                          <>
                            <Copy className="h-4 w-4 mr-2" />
                            复制代码
                          </>
                        )}
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto">
                      <code>{codeExamples[activeCodeExample as keyof typeof codeExamples].code}</code>
                    </pre>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          {/* 常见问题 */}
          <TabsContent value="faq" className="space-y-8">
            <div className="text-center space-y-4 mb-12">
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900">
                常见问题
              </h2>
              <p className="text-xl text-gray-600">
                解答开发者常见疑问
              </p>
            </div>
            
            <div className="max-w-4xl mx-auto space-y-6">
              {faqs.map((faq, index) => (
                <Card key={index}>
                  <CardHeader>
                    <CardTitle className="text-lg">{faq.question}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600">{faq.answer}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </section>

      {/* 技术支持 */}
      <section className="bg-gray-50">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center space-y-8">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900">
              需要帮助？
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              我们的技术团队随时为您提供支持，确保您的集成过程顺利进行
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                { icon: Headphones, title: '在线客服', desc: '7x24小时在线支持' },
                { icon: MessageSquare, title: '技术交流群', desc: '开发者社区讨论' },
                { icon: FileText, title: '技术文档', desc: '详细的集成指南' }
              ].map((support, index) => (
                <Card key={index} className="text-center">
                  <CardHeader>
                    <div className="mx-auto w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center mb-4">
                      <support.icon className="h-6 w-6 text-blue-600" />
                    </div>
                    <CardTitle className="text-lg">{support.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription>{support.desc}</CardDescription>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-r from-blue-600 to-purple-600 text-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center space-y-8">
            <h2 className="text-3xl lg:text-4xl font-bold">
              开始您的集成之旅
            </h2>
            <p className="text-xl opacity-90 max-w-2xl mx-auto">
              立即注册，获取API密钥，开始集成我们的短信服务
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                variant="secondary" 
                className="text-lg px-8 py-3"
                onClick={() => setShowLoginModal(true)}
              >
                立即开始
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button size="lg" variant="outline" className="text-lg px-8 py-3 border-white text-white hover:bg-white hover:text-blue-600" asChild>
                <Link to="/features">
                  查看功能
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* 登录 Modal */}
      <LoginModal 
        open={showLoginModal} 
        onOpenChange={setShowLoginModal} 
      />
    </div>
  )
}
